import * as ng from 'angular';
import * as Linq from 'linq-es5';
import * as ASM from '../data/interfaces';
import { IToastMessageService } from '../services';
import { ISettingsResource } from '../data';
import {IReCaptchaSettings} from "../data/interfaces";

let CustomerSettingsServiceFactory: Function = function ($q: ng.IQService,
    $log: ng.ILogService,
    Settings: ISettingsResource,
    toastMessageFactory: IToastMessageService): ICustomerSettingsService {
    return new CustomerSettingsService($q, $log, Settings, toastMessageFactory);
}

CustomerSettingsServiceFactory.$inject = ['$q', '$log', 'Settings', 'toastMessageFactory'];

let CustomerSettings = {
    name: "CustomerSettings",
    factory: CustomerSettingsServiceFactory
};

interface ICustomerSettingsService {
    getFrequentParkerSettings(clearCache?: boolean): ng.IPromise<ASM.ISettings<ASM.IFrequentParkerSettings>>;
    saveFrequentParkerSettings(fppSettings: ASM.ISettings<ASM.IFrequentParkerSettings>, message:string): ng.IPromise<ASM.ISettings<ASM.IFrequentParkerSettings>>;
    getReservedParkingSettings(): ng.IPromise<ASM.IReservedParkingSettings>;
    getContentSettings(): ng.IPromise<ASM.IContentSettings>;
    getCreditCardSettings(): ng.IPromise<ASM.ICreditCardSettings>;
    getReCaptchaSettings(): ng.IPromise<ASM.IReCaptchaSettings>;
    getLanguageSettings(): ng.IPromise<ASM.ILanguageSettings>;
    getGeneralSettings(clearCache?: boolean): ng.IPromise<ASM.ISettings<ASM.IGeneralSettings>>;
}

export class CustomerSettingsService implements ICustomerSettingsService {
    private FrequentParkerSettings: ASM.ISettings<ASM.IFrequentParkerSettings> = null;
    private ReservedParkingSettings: ASM.IReservedParkingSettings = null;
    private ContentSettings: ASM.IContentSettings = null;
    private CreditCardSettings: ASM.ICreditCardSettings = null;
    private LanguageSettings: ASM.ILanguageSettings = null;
    private GeneralSettings: ASM.ISettings<ASM.IGeneralSettings> = null;
    private ReCaptchaSettings: IReCaptchaSettings = null;

    constructor(private $q: ng.IQService,
        private $log: ng.ILogService,
        private Settings: ISettingsResource,
        private toastMessageFactory: IToastMessageService ) {
    }

    public getFrequentParkerSettings(clearCache?:boolean): ng.IPromise<ASM.ISettings<ASM.IFrequentParkerSettings>> {
        
        if (this.FrequentParkerSettings !== null && clearCache !== true) {
            return this.$q.resolve(this.FrequentParkerSettings);
        }
        else {
            return this.Settings.get({ key: 'FrequentParker' }).$promise
                .then((response) => {
                    this.FrequentParkerSettings = response as ASM.ISettings<ASM.IFrequentParkerSettings>;
                    this.synchronizeFPPSettingsPointers(this.FrequentParkerSettings.settings);

                    return this.$q.resolve(this.FrequentParkerSettings);
                })
                .catch((e) => {
                    this.$log.log(e, 'FrequentParkerSettingsService: Failed load of FrequentParkerSettings for current user');
                    return this.$q.resolve(e);
                });
        }
    }

    private synchronizeFPPSettingsPointers(fppSettings: ASM.IFrequentParkerSettings) {

        let groupQuery = Linq.From(fppSettings.groups)
            .Where(x => x.enabled);

        //syncronize the pointers
        fppSettings.promotionSettings.forEach(x => {
            var matches = [];
            if (x.groups) {
                x.groups.forEach(y => {
                    var match = groupQuery.FirstOrDefault(z => y.id == z.id);
                    if (match) {
                        matches.push(match);
                    }
                });
            }

            x.groups = [];
            x.groups = matches;
        });
    }

    public saveFrequentParkerSettings(fppSettings: ASM.ISettings<ASM.IFrequentParkerSettings>, message:string): ng.IPromise<ASM.ISettings<ASM.IFrequentParkerSettings>> {
        return this.Settings.update(fppSettings).$promise
            .then((response) => {
                this.toastMessageFactory.showSuccessMessage(message);
                this.FrequentParkerSettings = response as ASM.ISettings<ASM.IFrequentParkerSettings>;
                this.synchronizeFPPSettingsPointers(this.FrequentParkerSettings.settings);
                return this.$q.resolve(this.FrequentParkerSettings);
            })
            .catch((e) => {
                this.$log.log(e, 'FrequentParkerSettingsService: Failed save of FrequentParkerSettings for current user');             
                return this.$q.reject(e);
            });
    }

    public getReservedParkingSettings(): ng.IPromise<ASM.IReservedParkingSettings> {

        if (this.ReservedParkingSettings !== null) {
            return this.$q.resolve(this.ReservedParkingSettings);

        }
        else {
            return this.Settings.get({ key: 'ReservedParking' }).$promise
                .then((response) => {
                    this.ReservedParkingSettings = (response as ASM.ISettings<ASM.IReservedParkingSettings>)
                        .settings as ASM.IReservedParkingSettings;
                    console.log('Reserved Parking Settings', this.ReservedParkingSettings);
                    return this.$q.resolve(this.ReservedParkingSettings);
                })
                .catch((e) => {
                    this.$log.log(e, 'ReservedParkingSettingsService: Failed load of ReservedParkingSettings for current user');
                    return this.$q.resolve(e);
                });
        }
    }

    private contentSettingsPromise: ng.IPromise<ASM.IContentSettings> = null;
    private getContents(): ng.IPromise<ASM.IContentSettings> {

        if (this.contentSettingsPromise)
            return this.contentSettingsPromise;

        this.contentSettingsPromise = this.Settings.get({ key: 'ContentSettings' }).$promise
            .then((response) => {
                this.ContentSettings = (response as ASM.ISettings<ASM.IContentSettings>)
                    .settings as ASM.IContentSettings;
                console.log('Content Settings', this.ContentSettings);
                return this.$q.resolve(this.ContentSettings);
            })
            .catch((e) => {
                this.$log.log(e, 'ContentSettingsService: Failed load of ContentSettings for current user');
                return this.$q.resolve(e);
            });

        return this.contentSettingsPromise;
    };

    public getContentSettings(): ng.IPromise<ASM.IContentSettings> {

        if (this.ContentSettings !== null) {
            return this.$q.resolve(this.ContentSettings);

        }
        else {
            return this.getContents();
        }
    }

    public getCreditCardSettings(): ng.IPromise<ASM.ICreditCardSettings> {

        return this.getSettings<ASM.ICreditCardSettings>('CreditCard', () => {
                return this.CreditCardSettings
            },
            (creditCardSettings) => {
                this.CreditCardSettings = creditCardSettings;
            });
    }

    public getReCaptchaSettings(): ng.IPromise<IReCaptchaSettings> {

        return this.getSettings<ASM.IReCaptchaSettings>('ReCaptcha', () => {
                return this.ReCaptchaSettings;
            },
            (reCaptchaSettings) => {
                this.ReCaptchaSettings = reCaptchaSettings;
            });
    }

    private getSettings<TSettings>(settingsKey: string, getCurrentSettings: () => TSettings, setCurrentSettings: (settings: TSettings) => void): ng.IPromise<TSettings> {
        var currentSettings: TSettings = getCurrentSettings();

        if (currentSettings !== null) {
            return this.$q.resolve(currentSettings);
        }
        else {
            return this.Settings.get({key: settingsKey}).$promise
                .then((response) => {
                    var responseSettings = (response as ASM.ISettings<TSettings>).settings as TSettings
                    setCurrentSettings(responseSettings);

                    return this.$q.resolve(responseSettings);
                })
                .catch((e) => {
                    this.$log.log(e, 'CustomerSettingsService: Failed load of ' + settingsKey + ' for current user');
                    return this.$q.resolve(e);
                });
        }
    }
    
    private languageSettingsPromise: ng.IPromise<ASM.ILanguageSettings> = null;
    private getLanguages(): ng.IPromise<ASM.ILanguageSettings> {

        if (this.languageSettingsPromise)
            return this.languageSettingsPromise;

        this.languageSettingsPromise = this.Settings.get({ key: 'Languages' }).$promise
            .then((response) => {
                this.LanguageSettings = (response as ASM.ISettings<ASM.ILanguageSettings>)
                    .settings as ASM.ILanguageSettings;
                console.log('Language Settings', this.LanguageSettings);
                return this.$q.resolve(this.LanguageSettings);
            })
            .catch((e) => {
                this.$log.log(e, 'LanguageSettingsService: Failed load of LanguageSettings for current user');
                return this.$q.resolve(e);
            });

        return this.languageSettingsPromise;
    };
    
    public getLanguageSettings(): ng.IPromise<ASM.ILanguageSettings> {

        if (this.LanguageSettings !== null) {
            return this.$q.resolve(this.LanguageSettings);
        }
        else {
            return this.getLanguages();
        }
    }

    public getGeneralSettings(clearCache?: boolean): ng.IPromise<ASM.ISettings<ASM.IGeneralSettings>> {

        if (this.GeneralSettings !== null && clearCache !== true) {
            return this.$q.resolve(this.GeneralSettings);
        }
        else {
            return this.Settings.get({ key: 'GeneralSettings' }).$promise
                .then((response) => {
                    this.GeneralSettings = response as ASM.ISettings<ASM.IGeneralSettings>;

                    return this.$q.resolve(this.GeneralSettings);
                })
                .catch((e) => {
                    this.$log.log(e, 'GeneralSettingsService: Failed load of GeneralSettings for current user');
                    return this.$q.resolve(e);
                });
        }
    }

}

export { CustomerSettings, ICustomerSettingsService };
export default CustomerSettings;


// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/services/customer-settings-service.ts